import React, {useCallback, useMemo, useRef, useState} from 'react';
import {FormGroup} from 'shared/components/Forms/FormGroup';
import {Col, Form} from 'react-bootstrap';
import {Size} from 'shared/components/Forms/Size';
import {useField, useFormikContext} from 'formik';
import {useGetDrawerSystemQuery} from 'components/customer/Product/store/drawerRunnerApi';
import {useNotificationContext} from 'contexts/NotificationContext';
import {Drawer} from 'components/customer/Product/entity/Drawer';

interface DrawerPositionProps {
    index: number;
}

export const DrawerPosition = ({index}: DrawerPositionProps) => {
    const {addMessages} = useNotificationContext();
    const name = useRef('position_to_bottom');
    const {setFieldValue} = useFormikContext();
    const [isInvalid, setIsInvalid] = useState(false);

    const [{value: drawers}] = useField<Drawer[]>({
        name: 'drawers',
    });
    const [, {error}] = useField<number>({
        name: `drawers[${index}].${name.current}`,
    });

    const {data: drawerSystems} = useGetDrawerSystemQuery();

    const updateDrawerPosition = useCallback(
        (value: number) => {
            const drawer = drawers[Number(index)];

            if (drawer) {
                setIsInvalid(false);

                const difference = drawer.position_to_bottom - value;
                if (index == drawers.length - 1) {
                    const system = drawerSystems.find(
                        (system) => system.id == drawer.insert_id
                    );

                    if (system) {
                        void setFieldValue(
                            `drawers[${index}]`,
                            {
                                ...drawer,
                                [name.current]: value,
                                touched: true,
                                drawer_face_height:
                                    drawer.drawer_face_height + difference,
                            },
                            true
                        );
                    } else {
                        return {
                            isValid: false,
                            message: `Cannot find drawer information.`,
                        };
                    }
                } else {
                    void setFieldValue(
                        `drawers[${index}]`,
                        {
                            ...drawer,
                            [name.current]: value,
                            touched: true,
                            drawer_face_height:
                                drawer.drawer_face_height + difference,
                        },
                        true
                    );

                    const subsequentDrawer = drawers[Number(index) + 1];

                    if (subsequentDrawer) {
                        void setFieldValue(
                            `drawers[${Number(index) + 1}]`,
                            {
                                ...subsequentDrawer,
                                drawer_face_height:
                                    subsequentDrawer.drawer_face_height -
                                    difference,
                            },
                            true
                        );
                    }
                }

                return {isValid: true};
            }

            return {
                isValid: false,
                message: 'Invalid drawer position to bottom',
            };
        },
        [drawers, index, drawerSystems]
    );

    const updatePositionToBottom = useCallback(
        (_: string, value: number) => {
            if (value <= 0) {
                setIsInvalid(true);
                addMessages({
                    type: 'error',
                    messages: {
                        [index]: [
                            {
                                fields: [`drawers[${index}].${name.current}`],
                                message: 'Invalid drawer position to bottom.',
                            },
                        ],
                    },
                });

                return;
            }

            const {isValid, message} = updateDrawerPosition(value);
            if (!isValid) {
                setIsInvalid(true);
                addMessages({
                    type: 'error',
                    messages: {
                        [index]: [
                            {
                                fields: [`drawers[${index}].${name.current}`],
                                message:
                                    message ||
                                    'Invalid drawer position to bottom.',
                            },
                        ],
                    },
                });
            }
        },
        [index, setFieldValue, updateDrawerPosition]
    );

    const positionValue = useMemo(() => {
        if (drawers) {
            const drawer = drawers[Number(index)];

            if (drawer && name.current in drawer) {
                type positionKey = keyof typeof drawer;
                return Number(drawer[name.current as positionKey]);
            }
        }

        return 0;
    }, [drawers, index]);

    return (
        <FormGroup>
            <Form.Label column xs={5}>
                Position to bottom
            </Form.Label>
            <Col xs={7}>
                <Size
                    isInvalid={isInvalid || error !== undefined}
                    name={`drawers[${index}].${name.current}`}
                    value={positionValue}
                    metric="mm"
                    selectHandler={updatePositionToBottom}
                    fieldSetIndex={index}
                />
            </Col>
        </FormGroup>
    );
};
